import * as R from 'ramda';

import { isEmptyFieldFilter } from '@atom/components/common/workOrdersFilters/fieldFilterUtilities';
import {
  FieldFilter,
  TaskFieldsFilter,
  TaskFilter,
  WorkOrdersConnectionInput,
} from '@atom/types/work';

import {
  AdditionalFieldFilters,
  FilterForm,
  TasksAdditionalFieldFilters,
} from './WorkOrdersFiltersContext';

// A field the user filled in and then cleared is still tracked in the form -
// it must not reach the connection input as a valueless filter.
const buildFieldFilters = (fields: AdditionalFieldFilters): FieldFilter[] =>
  Object.values(fields).filter(field => !isEmptyFieldFilter(field));

const buildTaskFieldsFilters = (
  taskFields: TasksAdditionalFieldFilters,
): TaskFieldsFilter[] =>
  Object.entries(taskFields)
    .map(([taskId, fields]) => ({
      id: taskId,
      fields: buildFieldFilters(fields),
    }))
    .filter(task => !R.isEmpty(task.fields));

export const buildWorkOrdersFilterInput = (
  form: FilterForm,
): WorkOrdersConnectionInput => {
  const workTemplateFields = buildFieldFilters(form.workTemplateFields);
  const taskCommonFields = buildFieldFilters(form.taskCustomCommonFields);
  const taskFields = buildTaskFieldsFilters(form.workTemplateTaskFields);

  const workFilters: WorkOrdersConnectionInput = {
    ...(form.workDueDateStart && { dueDateStart: form.workDueDateStart }),
    ...(form.workDueDateEnd && { dueDateEnd: form.workDueDateEnd }),
    ...(form.workCompletionDateStart && {
      completionDateStart: form.workCompletionDateStart,
    }),
    ...(form.workCompletionDateEnd && {
      completionDateEnd: form.workCompletionDateEnd,
    }),
    ...(form.workAppliedDateStart && {
      appliedDateStart: form.workAppliedDateStart,
    }),
    ...(form.workAppliedDateEnd && { appliedDateEnd: form.workAppliedDateEnd }),
    ...(!R.isEmpty(form.selectStatuses) && { statusIds: form.selectStatuses }),
    ...(!R.isEmpty(form.selectPriorities) && {
      priorityIds: form.selectPriorities,
    }),
    ...(!R.isEmpty(form.workTemplateFolders) && {
      workTemplateFolderIds: form.workTemplateFolders.map(folder => folder.id),
    }),
    ...(!R.isNil(form.hasWorkTemplate) && {
      hasWorkTemplate: form.hasWorkTemplate,
    }),
    ...(!R.isEmpty(form.workTemplates) && {
      workTemplateIds: form.workTemplates.map(template => template.id),
    }),
    ...(!R.isEmpty(workTemplateFields) && { fields: workTemplateFields }),
  };

  const taskFilters: TaskFilter = {
    ...(form.taskDueDateStart && { dueDateStart: form.taskDueDateStart }),
    ...(form.taskDueDateEnd && { dueDateEnd: form.taskDueDateEnd }),
    ...(form.taskStartDateStart && { startDateStart: form.taskStartDateStart }),
    ...(form.taskStartDateEnd && { startDateEnd: form.taskStartDateEnd }),
    ...(form.taskCompletionDateStart && {
      completionDateStart: form.taskCompletionDateStart,
    }),
    ...(form.taskCompletionDateEnd && {
      completionDateEnd: form.taskCompletionDateEnd,
    }),
    ...(form.taskDurationStart && { durationStart: form.taskDurationStart }),
    ...(form.taskDurationEnd && { durationEnd: form.taskDurationEnd }),
    ...(!R.isEmpty(taskCommonFields) && { commonFields: taskCommonFields }),
    ...(!R.isEmpty(taskFields) && { each: taskFields }),
  };

  const teamFilters: WorkOrdersConnectionInput = {
    ...(!R.isEmpty(form.teamAssignedToGroupIds) && {
      groupIds: form.teamAssignedToGroupIds,
    }),
    ...(!R.isEmpty(form.teamAssignedToUserIds) && {
      userIds: form.teamAssignedToUserIds,
    }),
    ...(!R.isEmpty(form.teamCompletedByIds) && {
      completedByIds: form.teamCompletedByIds,
    }),
    ...(!R.isEmpty(form.teamCreatedByIds) && {
      createdByIds: form.teamCreatedByIds,
    }),
  };

  return {
    ...workFilters,
    ...(!R.isEmpty(taskFilters) && { tasks: taskFilters }),
    ...teamFilters,
  };
};
